'use client';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Must be deterministic for SSR + first client render to avoid a hydration mismatch.
  // The real saved theme is read in an effect below (the inline script in layout.tsx
  // already applies the `dark` class pre-hydration, so the page background doesn't flash).
  const [theme, setTheme] = useState<Theme>('light');
  const hydrated = useRef(false);

  // Sync the persisted theme after mount (client-only value).
  useEffect(() => {
    const saved = localStorage.getItem('theme') as Theme;
    if (saved === 'dark' || saved === 'light') setTheme(saved);
  }, []);

  // Persist + reflect on <html>. Skip the very first run so we don't overwrite the
  // saved value before the read effect above has applied it.
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}